import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templateStudioService } from '../services/template-studio.service';
import type {
  TemplateTreeResponse,
  TemplateDetail,
  TemplateRecord,
  TemplateVersion,
  GitFileHistoryEntry,
  GitDiffResult,
  AuditLogEntry,
} from '../types/template.types';

const KEYS = {
  templates: (tenantId: string) => ['template-studio', 'templates', tenantId] as const,
  template: (tenantId: string, templateId: string) => ['template-studio', 'template', tenantId, templateId] as const,
  history: (tenantId: string, templateId: string) => ['template-studio', 'history', tenantId, templateId] as const,
  audit: (tenantId: string, templateId: string) => ['template-studio', 'audit', tenantId, templateId] as const,
};

export function useTemplates(tenantId: string | undefined) {
  return useQuery({
    queryKey: KEYS.templates(tenantId ?? ''),
    queryFn: () => templateStudioService.listTemplates(tenantId!),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useTemplate(tenantId: string | undefined, templateId: string | undefined) {
  return useQuery({
    queryKey: KEYS.template(tenantId ?? '', templateId ?? ''),
    queryFn: () => templateStudioService.getTemplate(tenantId!, templateId!),
    enabled: !!tenantId && !!templateId,
    staleTime: 15_000,
  });
}

export function useTemplateHistory(tenantId: string | undefined, templateId: string | undefined) {
  return useQuery({
    queryKey: KEYS.history(tenantId ?? '', templateId ?? ''),
    queryFn: () => templateStudioService.getHistory(tenantId!, templateId!),
    enabled: !!tenantId && !!templateId,
    staleTime: 30_000,
  });
}

export function useTemplateAudit(tenantId: string | undefined, templateId: string | undefined) {
  return useQuery({
    queryKey: KEYS.audit(tenantId ?? '', templateId ?? ''),
    queryFn: () => templateStudioService.getAuditLog(tenantId!, templateId!),
    enabled: !!tenantId && !!templateId,
    staleTime: 30_000,
  });
}

export function useCreateTemplate(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof templateStudioService.createTemplate>[1]) =>
      templateStudioService.createTemplate(tenantId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.templates(tenantId) });
    },
  });
}

export function useUpdateContent(tenantId: string, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { content: string; commitMessage: string }) =>
      templateStudioService.updateContent(tenantId, templateId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.template(tenantId, templateId) });
      qc.invalidateQueries({ queryKey: KEYS.history(tenantId, templateId) });
      qc.invalidateQueries({ queryKey: KEYS.audit(tenantId, templateId) });
    },
  });
}

export function useUpdateMetadata(tenantId: string, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      templateStudioService.updateMetadata(tenantId, templateId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.template(tenantId, templateId) });
      qc.invalidateQueries({ queryKey: KEYS.templates(tenantId) });
      qc.invalidateQueries({ queryKey: KEYS.audit(tenantId, templateId) });
    },
  });
}

export function useDeleteTemplate(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) =>
      templateStudioService.deleteTemplate(tenantId, templateId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.templates(tenantId) });
    },
  });
}

export function useForkTemplate(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { sourceTemplateId: string; name?: string }) =>
      templateStudioService.forkTemplate(tenantId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.templates(tenantId) });
    },
  });
}

export function useActivateVersion(tenantId: string, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      templateStudioService.activateVersion(tenantId, templateId, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.template(tenantId, templateId) });
      qc.invalidateQueries({ queryKey: KEYS.templates(tenantId) });
    },
  });
}

export function useTestRender(tenantId: string, templateId: string) {
  return useMutation({
    mutationFn: (data: { content: string; sampleData: Record<string, unknown>; subjectLine?: string }) =>
      templateStudioService.testRender(tenantId, templateId, data),
  });
}

export function useDiffVersions(tenantId: string, templateId: string) {
  return useMutation({
    mutationFn: (data: { fromSha: string; toSha: string }) =>
      templateStudioService.getDiff(tenantId, templateId, data.fromSha, data.toSha),
  });
}
